/**
 * Pure state machine for the Testdata Generator logo choice. Kept
 * dependency-free (no React imports) so it can be unit-tested without a
 * frontend test framework — see `logoSelection.test.ts` (runs via Node's
 * built-in `node:test`).
 *
 * `useLogoState.ts` is the only caller; it owns the React state, the token
 * counter and every fetch. These functions never perform side effects.
 *
 * Rules (the logo must always belong to the current supplier or to an
 * explicit user choice):
 * - Every transition that changes the choice (party selection, start of an
 *   upload, explicit default logo, delete, supplier change) stores a fresh,
 *   strictly increasing `token` in the state. An asynchronous result
 *   (party-logo fetch, FileReader) is only accepted when it carries the token
 *   that is still in the state — a late response for a previously selected
 *   party can never overwrite the current choice. Transitions that turn out
 *   to be a no-op keep the existing token, so they cannot cancel unrelated
 *   async work.
 * - Selecting a party invalidates the previous logo immediately; while the
 *   party's own logo is being fetched the choice is `pending` and no logo is
 *   submitted.
 * - A party without a logo, a failed fetch, or a cleared choice means no
 *   logo. There is no implicit fallback to another party's logo or to the
 *   user's saved default logo; the default logo is only used when chosen
 *   explicitly (`source: 'saved'`).
 */

export type LogoSource = 'none' | 'party' | 'manual' | 'saved'

export interface LogoSelection {
  source: LogoSource
  base64: string | null
  fileName: string
  /** Party preset the logo (or the pending fetch) belongs to; `null` otherwise. */
  partyId: number | null
  /** True while the selected party's own logo is still being fetched. */
  pending: boolean
  token: number
}

export const INITIAL_LOGO_SELECTION: LogoSelection = {
  source: 'none', base64: null, fileName: '', partyId: null, pending: false, token: 0,
}

function noLogo(token: number): LogoSelection {
  return { ...INITIAL_LOGO_SELECTION, token }
}

/** User deleted the logo, or the supplier changed outside the party dropdown
 * (template/history/config preset, other user): no logo, cancels any pending fetch. */
export function clearLogoSelection(token: number): LogoSelection {
  return noLogo(token)
}

/** A party was selected as supplier. The previous logo (of any source) is
 * dropped immediately; a party with a logo becomes `pending` until resolved. */
export function selectPartyLogo(
  token: number, partyId: number, partyHasLogo: boolean,
): LogoSelection {
  return { ...noLogo(token), partyId, pending: partyHasLogo }
}

/** Result of the party-logo fetch. `base64 === null` covers "party has no
 * logo after all" and a failed request — both mean no logo. Ignored unless it
 * answers the still-current pending request for that same party. */
export function resolvePartyLogo(
  state: LogoSelection, token: number, partyId: number, base64: string | null, fileName: string,
): LogoSelection {
  if (state.token !== token || !state.pending || state.partyId !== partyId) return state
  if (!base64) return { ...state, pending: false }
  return { ...state, source: 'party', base64, fileName, pending: false }
}

/** The supplier's identity (name/GLN) was edited by hand or the party
 * selection was cleared: a party-derived logo (or pending fetch) no longer
 * belongs to the visible supplier. An explicit manual/default choice stays. */
export function invalidatePartyLogo(state: LogoSelection, token: number): LogoSelection {
  if (state.source !== 'party' && !state.pending) return state
  return noLogo(token)
}

/** The user picked a file to upload: the previous choice (including a
 * still-pending party fetch) is replaced; the file itself arrives via
 * `applyManualLogo` with this same token. */
export function beginManualUpload(token: number): LogoSelection {
  return noLogo(token)
}

/** Manual upload finished reading. `token` is the one from
 * `beginManualUpload`; if the choice changed meanwhile (other supplier,
 * delete, default logo, ...) the file is discarded. */
export function applyManualLogo(
  state: LogoSelection, token: number, base64: string, fileName: string,
): LogoSelection {
  if (state.token !== token) return state
  return { ...noLogo(token), source: 'manual', base64, fileName }
}

/** The user explicitly chose the saved personal default logo. */
export function applySavedLogo(
  token: number, base64: string, fileName: string,
): LogoSelection {
  return { ...noLogo(token), source: 'saved', base64, fileName }
}

/** The current manual logo was just stored as the default logo — the same
 * image, now labelled as the default. Only applies if it is still shown. */
export function markManualLogoSaved(state: LogoSelection, base64: string): LogoSelection {
  if (state.source !== 'manual' || state.base64 !== base64) return state
  return { ...state, source: 'saved' }
}

/** The saved default logo was deleted: if it is the current choice, drop it
 * (no other logo takes its place). */
export function dropSavedLogo(state: LogoSelection, token: number): LogoSelection {
  return state.source === 'saved' ? noLogo(token) : state
}

/** Exactly what is submitted as `logo_base64`. Never a logo while pending. */
export function logoForPayload(state: LogoSelection): string | undefined {
  if (state.pending || state.source === 'none') return undefined
  return state.base64 ?? undefined
}
